import '@zebra-fed/icons/Enterprise/index.css';
import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

// Internal imports
import { AuthContext } from '../../../authentication/Auth';
import { getTotalCount, fetchLastPage, fetchDevices, getDeviceType } from './deviceReportQueries';
import { RESOURCES, withRBAC, usePermissions } from '../../../authentication/rbac';
import { DEFAULT_ORDER_BY, DEFAULT_ROWS_PER_PAGE, DEFAUTL_ORDER_DIRECTION, DEVICE_TYPE_STATUS, DEVICE_TYPE_VALUE } from './DeviceReports.constant'

// Components
import { ButtonGroup, CommonButton } from '../../../components/common/Button';
import BottomNavLinks from '../../../components/BottomNavLinks';
import NumberToDate from '../../users/components/NumberToDate';
import Toolbar from '../../../components/common/Toolbar';
import Table from '../../../components/common/Table';
import DropdownCustom from '../../../components/DropdownCustom';
import firebaseUtil from "../../../data/FirebaseUtil";
import { getURLFromGCSBucket } from "../../commonUtils/ExportUtils"
import { useDGAMLicenseCheck } from '../../../authentication/licensing';
import NoAccessMessage from "../../../components/common/NoAccessMessage";

import {
    ContentWrapper,
    CommonPageHeader,
    CommonPageTitle,
    SnackbarContent,
    CheckingAccessMessage,
} from '../../../styled-components/shared';
import app from '../../../authentication/firebaseStart';
import { Snackbar } from '@material-ui/core';

const MainContent = styled.div`
  border-bottom: none;
  border: 1px solid #e0e3e9;
  margin-top: 10px;
  @media screen and (max-width: 750px) {
    margin-top: 60px;
  }
`;

const CuzCell = styled.p`
  font-size: 14px;
  font-weight: 400;
  overflow: hidden;
  white-space: nowrap;
  line-height: 20px;
  padding: 0px 0px 0px 16px;;
  -o-text-overflow: ellipsis;
  -ms-text-overflow: ellipsis;
  text-overflow: ellipsis;
`;

const CustomHeader = styled.p`
  padding: 0px 0px 0px 16px;
  font-weight: bold;
  text-wrap-mode: nowrap;
  align-self: 'center';
  text-align: ${props => (props.left ? 'left' : 'center')};
`;

const imgStyleLoader = {
    width: 18,
    height: 18,
    marginTop: 6,
    marginLeft: -15,
    marginRight: 5,
}


function DeviceReportsPage() {
    const context = useContext(AuthContext);
    const { t } = useTranslation();
    const DEVICE_TYPE = [
        { label: t('menu.subMenu.allDevices'), value: DEVICE_TYPE_VALUE.typeAll },
        { label: `${t('missing')} ${t('devices')}`, value: DEVICE_TYPE_VALUE.typeMissing },
        { label: t('lostDevices'), value: DEVICE_TYPE_VALUE.typeLost },
        { label: t('decommissionedDevices'), value: DEVICE_TYPE_VALUE.typeDcmd },
        { label: t('recommissionedDevices'), value: DEVICE_TYPE_VALUE.typeRcmd },
    ]
    const [data, setData] = useState([]);
    const [pageSize, setPageSize] = useState(DEFAULT_ROWS_PER_PAGE);
    const [currentPage, setCurrentPage] = useState(0);
    const [itemCount, setItemCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [searchField, setSearchField] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [deviceType, setDeviceType] = useState(DEVICE_TYPE[0].value);
    const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);
    const [orderDirection, setOrderDirection] = useState(DEFAUTL_ORDER_DIRECTION);
    const [cursors, setCursors] = useState(null);
    const [snackbarVisible, setSnackBarVisible] = useState(false);
    const [exportBtnState, setexportBtnState] = useState('none');
    const snackBarContent = useRef();
    const isSnackbarFailure = useRef();
    const companyID = context.currentUser.claims.companyID;
    const firestore = app.firestore();
    const {
        canReadDeviceReports,
        canExportDeviceReports,
    } = usePermissions();
    const { isDGAMLicensed, loadingLicense } = useDGAMLicenseCheck();
    useEffect(() => {
        if (!canReadDeviceReports) {
            return; // TODO: redirect/show unauthorized message
        }
        initManageDevices();
    }, [deviceType]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!searchText) {
            setOrderBy(DEFAULT_ORDER_BY);
            setSearchField(null);
        } else {
            setOrderBy(searchField);
        }
    }, [searchText, searchField]);

    // Helper Functions
    async function initManageDevices() {
        getData(pageSize, orderBy, orderDirection, true);
    }

    async function getData(currentPageSize, orderBy = DEFAULT_ORDER_BY, orderDirection = 'asc', shouldRefreshCount = false, currentSearchText = searchText, currentSearchField = searchField) {
        setIsLoading(true);
        try {
            setCurrentPage(0);
            // Get fresh count if needed
            if (shouldRefreshCount || itemCount === 0) {
                const count = await getTotalCount(context, {
                    searchText: currentSearchText,
                    searchField: currentSearchField,
                    orderDirection,
                    deviceType,
                    orderBy,
                });
                setItemCount(count);


            }
            const { devices, cursors: newCursors } = await fetchDevices({
                context,
                deviceType,
                limit: currentPageSize,
                searchField: currentSearchField,
                searchText: currentSearchText,
                orderBy,
                orderDirection
            });
            setData(devices);
            setCursors(newCursors);
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching devices:', error);
            setIsLoading(false);
        }
    }

    async function next() {
        if (!cursors?.last) return;
        setIsLoading(true);
        setCurrentPage(prev => prev + 1);
        try {
            const { devices, cursors: newCursors } = await fetchDevices({
                context,
                deviceType,
                limit: pageSize,
                searchField,
                searchText,
                orderBy: searchField || orderBy,
                orderDirection,
                startAfterDoc: cursors.last,
                totalCount: itemCount
            });

            if (devices.length > 0) {
                setData(devices);
                setCursors(newCursors);
            }
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching next page:', error);
            setIsLoading(false);
        }
    }

    async function prev() {
        if (!cursors?.first) return;
        setIsLoading(true);
        setCurrentPage(prev => prev - 1);
        try {
            const { devices, cursors: newCursors } = await fetchDevices({
                context,
                deviceType,
                limit: pageSize,
                searchField,
                searchText,
                orderBy: searchField || orderBy,
                orderDirection,
                endBeforeDoc: cursors.first,
                totalCount: itemCount
            });

            if (devices.length > 0) {
                setData(devices);
                setCursors(newCursors);
            }
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching previous page:', error);
            setIsLoading(false);
        }
    }

    async function handleFirstPage() {
        setIsLoading(true);
        setCurrentPage(0);
        try {
            const { devices, cursors: newCursors } = await fetchDevices({
                context,
                deviceType,
                limit: pageSize,
                searchField,
                searchText,
                orderBy: searchField || orderBy,
                orderDirection,
                totalCount: itemCount
            });

            setData(devices);
            setCursors(newCursors);
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching first page:', error);
            setIsLoading(false);
        }
    }

    async function handleLastPage() {
        setIsLoading(true);
        try {
            const result = await fetchLastPage({
                context,
                deviceType,
                limit: pageSize,
                searchField,
                searchText,
                orderBy: searchField || orderBy,
                orderDirection,
                startAfterDoc: cursors.last,
                totalCount: itemCount
            });

            if (result.users.length > 0) {
                setData(result.users);
                setCursors(result.cursors);

                const totalPages = Math.ceil(itemCount / pageSize);
                const lastPageNumber = Math.max(0, totalPages - 1);
                setCurrentPage(lastPageNumber);
            } else {
                setData([]);
                setCursors(null);
                setCurrentPage(0);
            }

            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching last page:', error);
            setData([]);
            setCursors(null);
            setCurrentPage(0);
            setIsLoading(false);
        }
    }

    function handleSearch(text, field) {
        setSearchText(text);
        setSearchField(field);
        handleFetchWithSearch(text, field);
    }

    function handleClearSearch() {
        setSearchText('');
        setSearchField(null);
        getData(pageSize, DEFAULT_ORDER_BY, orderDirection, true, '', null);
    }

    async function handleFetchWithSearch(text, field) {
        setIsLoading(true);
        setCurrentPage(0);
        setCursors(null);
        try {
            // Always get fresh count for new searches
            const count = await getTotalCount(context, { searchText: text, searchField: field, orderDirection, deviceType, orderBy });
            setItemCount(count);
            const { devices, cursors: newCursors } = await fetchDevices({
                context,
                deviceType,
                limit: pageSize,
                searchField: field,
                searchText: text,
                orderBy: field,
                orderDirection,
                totalCount: count
            });
            setData(devices);
            setCursors(newCursors);
            setIsLoading(false);
        } catch (error) {
            console.error('Error searching devices:', error);
            setIsLoading(false);
            setItemCount(0);
            setData([]);
        }
    }

    async function handleSort(field, direction) {
        if (searchText && field !== searchField) return;
        setIsLoading(true);
        setCurrentPage(0);
        setCursors(null); // Reset cursors on sort change
        try {
            setOrderBy(field);
            setOrderDirection(direction);
            const { devices, totalCount, cursors: newCursors } = await fetchDevices({
                context,
                deviceType,
                limit: pageSize,
                searchField,
                searchText,
                orderBy: field,
                orderDirection: direction
            });
            setData(devices);
            setItemCount(totalCount);
            setCursors(newCursors);
            setIsLoading(false);
        } catch (error) {
            console.error('Error sorting devices:', error);
            setIsLoading(false);
        }
    }

    async function showSnackBar(content, isFailure) {
        snackBarContent.current = content;
        isSnackbarFailure.current = isFailure;
        setSnackBarVisible(true);
    }

    async function exportcsv() {
        let data = {
            entityType: 'devices',            
            searchField: null,
            searchText: '',
            kioskDevicesReport: true,
            kioskDevicesState: getDeviceType(deviceType)            
        };
        setexportBtnState('exporting')
        let response = await firebaseUtil.exportConfiguration(data, t);
        if (response?.status) {
            let url = response.msg.filePath;
            let jobId = response.msg.jobId;
            const ref = firestore.collection('tenants').doc(companyID).collection('Jobs').doc(jobId);
            ref.onSnapshot((querySnapshot) => {
                let items = querySnapshot.data();
                if (items.status === 2) {
                    async function getURL() {
                        setexportBtnState('down')
                        let result = await getURLFromGCSBucket(url, companyID, "devices");
                        setexportBtnState('none')
                    }
                    getURL();
                }
            });
        } else {
            setexportBtnState('none')
            showSnackBar(t("error"), true);
        }
    }

    // Table Columns Configuration
    const columns = [
        {
            title: <CustomHeader left>{t('deviceDetail.deviceName')}</CustomHeader>,
            field: '__name__',
            searchable: false,
            sorting: true,
            render: rowData => (
                <CuzCell>{rowData.__name__}</CuzCell>
            ),
        },
        {
            title: <CustomHeader left>{t('username')}</CustomHeader>,
            field: 'userId',
            searchable: true,
            sorting: true,
            render: rowData => (
                <CuzCell>{rowData.userId}</CuzCell>
            ),
        },
        {
            title: <CustomHeader left>{t('kiosk')}</CustomHeader>,
            field: 'kioskName',
            searchable: false,
            sorting: true,
            render: rowData => (
                <CuzCell>{rowData.kioskName}</CuzCell>
            ),
        },
        {
            title: <CustomHeader left>{t('menu.subMenu.site')}</CustomHeader>,
            field: 'siteName',
            searchable: false,
            sorting: true,
            render: rowData => (
                <CuzCell>{rowData.siteName}</CuzCell>
            ),
        },
        {
            title: <CustomHeader>{t('lastStatusUpdate')}</CustomHeader>,
            field: 'lastStatusUpdate',
            sorting: true,
            cellStyle: { textAlign: 'center' },
            render: rowData => (
                <CuzCell>
                    <NumberToDate
                        style={{ fontSize: '14px' }}
                        timestamp={rowData.lastStatusUpdate}
                    />
                </CuzCell>
            ),
        },
        {
            title: <CustomHeader>{t('Status Reason')}</CustomHeader>,
            field: 'stateChangeReason',
            sorting: true,
            cellStyle: { textAlign: 'center' },
            render: rowData => (
                <CuzCell >
                    {rowData?.stateChangeReason}
                </CuzCell>
            ),
        },
        {
            title: <CustomHeader>{t('status')}</CustomHeader>,
            field: 'deviceStatusText',
            sorting: false,
            cellStyle: { textAlign: 'center' },
            render: rowData => (
                <CuzCell style={{ color: [DEVICE_TYPE_STATUS.statusMissing, DEVICE_TYPE_STATUS.statusLost].includes(rowData?.deviceStatusText) ? '#D70015' : rowData?.deviceStatusText === DEVICE_TYPE_STATUS.statusDcmd ? '#7E0CFF' : '#00864F' }}>
                    {rowData?.deviceStatusText}
                </CuzCell>
            ),
        },
    ];


    return (
        <ContentWrapper
            style={{
                cursor: loadingLicense ? "wait" : "default",
                opacity: loadingLicense ? "0.4" : "1",
            }}
        >
            <CommonPageHeader>
                <CommonPageTitle>{t('devices')}</CommonPageTitle>
                {isDGAMLicensed && !loadingLicense && canExportDeviceReports &&
                    <ButtonGroup>
                        <CommonButton
                            type="outlined"
                            onClick={exportcsv}
                        >
                            {exportBtnState != 'none' ?
                                <img src={process.env.PUBLIC_URL + '/csv_export.gif'} style={imgStyleLoader} /> : ""}
                            {`${exportBtnState == 'none' ? t('exportingData') :
                                exportBtnState == 'exporting' ? t('exporting') :
                                    exportBtnState == 'down' ? t('downloading') : null}`}

                        </CommonButton>
                    </ButtonGroup>}
            </CommonPageHeader>

            {!isDGAMLicensed && !loadingLicense && (
                <NoAccessMessage>
                    {t('licenseUpgradeText')}
                </NoAccessMessage>
            )}

            {isDGAMLicensed && !loadingLicense && (
                <>
                    <div className="dropdown-container">
                        <span className="devices-dash-drop" style={{ zIndex: '10' }}>

                            <Toolbar
                                searchText={searchText}
                                onSearch={(searchText, searchField) => {
                                    handleSearch(searchText, searchField);
                                }}
                                onClear={() => {
                                    handleClearSearch();
                                }}
                                multiSearchOpts={[
                                    { name: t('deviceDetail.deviceName'), value: '__name__' },
                                    { name: t('kiosk'), value: 'kioskName' },
                                    { name: t('menu.subMenu.site'), value: 'siteName' },
                                ]}
                                size={'medium'}
                                placeholderText={t(`${t('search')} ${t('deviceDetail.deviceName')}/${t('kiosk')}/${t('menu.subMenu.site')}`)}
                            />

                            <DropdownCustom
                                selectOpts={DEVICE_TYPE}
                                onChange={(e) => {
                                    setDeviceType(e.target.value)
                                }}
                                selectVal={deviceType}
                                defaultValue={DEVICE_TYPE[0]}
                                containerStyle={{ "width": "300" }}

                            />
                        </span>
                    </div>
                    <MainContent>
                        <Table
                            columns={deviceType === 'all' ? columns : columns.filter((col) => !['deviceStatusText'].includes(col?.field))}
                            data={data}
                            isLoading={isLoading}
                            orderBy={orderBy}
                            orderDirection={orderDirection}
                            isSearchActive={!!searchText}
                            searchField={searchField}
                            onSort={(field, direction) => {
                                handleSort(field, direction)
                            }}
                            pagination={{
                                itemCount,
                                currentPage,
                                rowsPerPage: pageSize,
                                rowsPerPageOptions: [10, 20, 50],
                                onNextPageClicked: next,
                                onPrevPageClicked: prev,
                                onFirstPageClicked: handleFirstPage,
                                onLastPageClicked: handleLastPage,
                                onChangeRowsPerPage: (event) => {
                                    const newSize = event.target.value;
                                    setPageSize(newSize);
                                    setCursors(null);
                                    getData(newSize, orderBy, orderDirection, false, searchText, searchField);
                                },
                                hidePagination: itemCount <= DEFAULT_ROWS_PER_PAGE
                            }}
                            emptyMessage={t('messages.error.err26')}
                        />
                    </MainContent>
                    <div>
                        <Snackbar
                            open={snackbarVisible}
                            autoHideDuration={5000}
                            onClose={() => {
                                setSnackBarVisible(false);
                            }}
                        >
                            <SnackbarContent error={isSnackbarFailure.current ? true : false}>
                                {snackBarContent.current}
                            </SnackbarContent>
                        </Snackbar>
                    </div>
                </>
            )}
            <BottomNavLinks />
        </ContentWrapper>
    );
}

export default withRBAC(DeviceReportsPage, RESOURCES.DEVICE_REPORT);
